import axios, {AxiosHeaders, AxiosRequestConfig} from 'axios';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import Fortress from '@/services/fortress/Fortress';
import mapKeysToCase from '@/utils/mapKeysToCase';
import {getEnv} from '@/config/environment';
import {addGeneralRequestInterceptor} from './axiosInterceptors';
import brandIdEmitter from './brandIdEventEmitter';

const contentStudioInstance = axios.create({
  baseURL: getEnv().VITE_CONTENT_STUDIO_API_HOST || 'http://localhost:8001',
});

const NO_INTERCEPTION_URLS = [
  '/api/v1/assets/bulk-upload-urls',
  '/api/v1/rephrasing/text_to_speech',
  '/api/v1/assets/download-zip',
];
const BRAND_ID_HEADER = 'x-brand-id';
const LTX_AUTH_TOKEN_HEADER = 'x-lightricks-auth-token';
const BROWSER_DELTA_ATTRIBUTES_HEADER = 'browser-delta-attributes-json';

addGeneralRequestInterceptor(
  contentStudioInstance,
  async (headers: AxiosHeaders, config?: AxiosRequestConfig) => {
    const ltxAuthToken = await Fortress.getAccessToken();
    headers.set('Accept', 'application/json;version=5');
    if (ltxAuthToken) {
      headers.set(LTX_AUTH_TOKEN_HEADER, ltxAuthToken);
    }

    // Allow request-specific brand ID override
    const requestBrandId = config?.headers?.[BRAND_ID_HEADER];
    if (requestBrandId) {
      headers.set(BRAND_ID_HEADER, requestBrandId as string);
    }

    if (AnalyticsService.getProvider('delta')) {
      headers.set(
        BROWSER_DELTA_ATTRIBUTES_HEADER,
        JSON.stringify(AnalyticsService.getBrowserBaseAttributes())
      );
    }
    return headers;
  }
);

brandIdEmitter.subscribe((brandId) => {
  if (brandId) {
    contentStudioInstance.defaults.headers.common[BRAND_ID_HEADER] = brandId;
  } else {
    delete contentStudioInstance.defaults.headers.common[BRAND_ID_HEADER];
  }
});

contentStudioInstance.interceptors.response.use(
  (response) => {
    const url = response.config.url || '';
    if (shouldExcludeFromMapping(url)) {
      return response;
    }
    return {...response, data: mapKeysToCase(response.data)};
  },
  (error) => {
    return Promise.reject(error);
  }
);

const shouldExcludeFromMapping = (url: string) => {
  return NO_INTERCEPTION_URLS.includes(url) || isDownloadOutputUrl(url);
};

const isDownloadOutputUrl = (url: string) => {
  return /^\/api\/v1\/projects\/.*\/download-output/.test(url);
};

export default contentStudioInstance;
